import React, { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import { useNavigate, useLocation } from 'react-router-dom';
import { TabItem, TabMenu } from '@/UIlibrary/TabMenu/TabMenu';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Information from './Information/Information';
import InviteMembers from './InviteMembers';

export const TAB_INVITE_MEMBERS = 'inviteMembers';
export const TAB_INFORMATION = 'information';
export type TAB_KEYS = typeof TAB_INVITE_MEMBERS | typeof TAB_INFORMATION;

const TAB_CONFIGS = {
	inviteMembers: {
		key: 'inviteMembers' as TAB_KEYS,
		translationKey: 'InviteMembers',
		content: () => <InviteMembers />
	},
	information: {
		key: 'information' as TAB_KEYS,
		translationKey: 'Information',
		content: () => <Information />
	}
};

const Settings: React.FC = () => {
	const t = useTranslations('Settings');
	const [tabs, setTabs] = useState<TabItem[]>([]);
	const [activeTab, setActiveTab] = useState(0);
	const navigate = useNavigate();
	const location = useLocation();

	const getTabs = (): TabItem[] => {
		return Object.values(TAB_CONFIGS).map((tabConfig) => ({
			title: t(toTranslationKey(tabConfig.translationKey)),
			content: tabConfig.content(),
			badgeVisible: false
		}));
	};

	const getStartingTab = (index: number) => {
		switch (index) {
			case 0:
				return TAB_INVITE_MEMBERS;
			case 1:
				return TAB_INFORMATION;
			default:
				return TAB_INVITE_MEMBERS;
		}
	};

	const setActiveTabIndex = (newTabs: TabItem[]) => {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has('startingTab')) {
			const currentTab = urlParams.get('startingTab') as TAB_KEYS;
			const currentTabIndex = newTabs.findIndex(
				(tab) => tab.title === t(toTranslationKey(TAB_CONFIGS[currentTab].translationKey))
			);
			setActiveTab(currentTabIndex !== -1 ? currentTabIndex : 0);
		} else {
			setActiveTab(0);
		}
	};

	const handleTabChange = (index: number) => {
		setActiveTab(index);
		const tabKey = Object.keys(TAB_CONFIGS).find(
			(key) =>
				t(toTranslationKey(TAB_CONFIGS[key as TAB_KEYS].translationKey)) === tabs[index].title
		) as TAB_KEYS;

		if (tabKey) {
			const params = new URLSearchParams();
			params.set('startingTab', getStartingTab(index));
			navigate(`${location.pathname}?${params.toString()}`);
		}
	};

	useEffect(() => {
		const newTabs = getTabs();
		setTabs(newTabs);
		setActiveTabIndex(newTabs);
	}, [t]);

	return (
		<main className="p-3 md:p-8">
			<div className="flex flex-col gap-6">
				<Typography type="display">{t('Title')}</Typography>
				<TabMenu
					activeTabIndex={activeTab}
					tabs={tabs}
					maxWidth={1500}
					onTabChanged={handleTabChange}
				/>
			</div>
		</main>
	);
};

export default Settings;
